import { createReducer, on } from '@ngrx/store';
import * as Actions from './tenant-independent-content-group-edit-screen.actions';
import { ContentGroupFilterV2ApiModel } from '@cxb/editor-stb-api';
import { ContentGroupFilterValueApiModel, ContentGroupPreviewV2ApiModel, ContentGroupV2ApiModel } from '@cxb/editor-stb-api/model/models';

export const featureKey = 'tenantIndependentContentGroupEditScreen';

export interface FeatureState {
  loading: boolean;
  programs: Array<ContentGroupPreviewV2ApiModel>;
  searchedPrograms: Array<ContentGroupPreviewV2ApiModel>;
  contentGroup: ContentGroupV2ApiModel;
  availableFilterOptions: Array<ContentGroupFilterValueApiModel>;
  originContentGroupFilter: Array<ContentGroupFilterV2ApiModel>;
  currentContentGroupFilter: Array<ContentGroupFilterV2ApiModel>;
  originPinnedItems: Array<string>;
  originIsDynamic: boolean;
  loadingPreview: boolean;
  loadingSearch: boolean;
  isFilterFormValid: boolean;
}

export interface AppState {
  [featureKey]: FeatureState;
}

export const initialState: FeatureState = {
  loading: false,
  programs: undefined,
  searchedPrograms: undefined,
  contentGroup: undefined,
  availableFilterOptions: undefined,
  originContentGroupFilter: undefined,
  currentContentGroupFilter: undefined,
  originPinnedItems: undefined,
  originIsDynamic: undefined,
  loadingPreview: false,
  loadingSearch: false,
  isFilterFormValid: true,
};

export const reducer = createReducer(
  initialState,
  on(Actions.startLoadingEditScreen, (state): FeatureState => ({ ...state, loading: true })),
  on(Actions.finishLoadingEditScreen, (state): FeatureState => ({ ...state, loading: false })),
  on(
    Actions.setContentGroup,
    (state, { contentGroup }): FeatureState => ({
      ...state,
      contentGroup,
    })
  ),
  on(
    Actions.startLoadingPreview,
    (state): FeatureState => ({
      ...state,
      loadingPreview: true,
    })
  ),
  on(
    Actions.finishLoadingPreview,
    (state): FeatureState => ({
      ...state,
      loadingPreview: false,
    })
  ),
  on(
    Actions.startLoadingSearch,
    (state): FeatureState => ({
      ...state,
      loadingSearch: true,
    })
  ),
  on(
    Actions.finishLoadingSearch,
    (state): FeatureState => ({
      ...state,
      loadingSearch: false,
    })
  ),
  on(
    Actions.setOriginContentGroupFilter,
    (state, { originContentGroupFilter }): FeatureState => ({
      ...state,
      originContentGroupFilter,
    })
  ),
  on(
    Actions.setCurrentContentGroupFilter,
    (state, { currentContentGroupFilter }): FeatureState => ({
      ...state,
      currentContentGroupFilter,
    })
  ),
  on(
    Actions.setOriginPinnedItems,
    (state, { originPinnedItems }): FeatureState => ({
      ...state,
      originPinnedItems,
    })
  ),
  on(
    Actions.setOriginIsDynamic,
    (state, { originIsDynamic }): FeatureState => ({
      ...state,
      originIsDynamic,
    })
  ),
  on(
    Actions.setIsDynamic,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        isDynamic: action.isDynamic,
      },
    })
  ),
  on(
    Actions.setContentGroupFilters,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        filters: action.filters,
      },
    })
  ),
  on(
    Actions.setContentGroupAvailableFilterOptions,
    (state, action): FeatureState => ({
      ...state,
      availableFilterOptions: action.availableFilterOptions,
    })
  ),
  on(
    Actions.updateContentGroupMeta,
    (state, { title, description }): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        title,
        description,
      },
    })
  ),
  on(Actions.setIsFilterFormValid, (state, { isFilterFormValid }) => ({
    ...state,
    isFilterFormValid,
  })),
  on(Actions.resetContentGroupEditScreenState, (): FeatureState => ({ ...initialState })),
  on(
    Actions.setContentGroupPrograms,
    (state, action): FeatureState => ({
      ...state,
      programs: action.programs,
    })
  ),
  on(
    Actions.setSearchedPrograms,
    (state, action): FeatureState => ({
      ...state,
      searchedPrograms: action.programs,
    })
  ),
  on(
    Actions.setPinnedContentIds,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        pinnedItems: [...action.contentIds],
      },
    })
  ),
  on(
    Actions.addPinnedContentId,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        pinnedItems: addContactIdToContentGroupPinnedContentIds(state.contentGroup.pinnedItems, action.contentId),
      },
    })
  ),
  on(
    Actions.deletePinnedContentId,
    (state, action): FeatureState => ({
      ...state,
      contentGroup: {
        ...state.contentGroup,
        pinnedItems: deleteContentIdFromContentGroupPinnedContentIds(state.contentGroup.pinnedItems, action.contentId),
      },
    })
  )
);

const deleteContentIdFromContentGroupPinnedContentIds = (pinnedContentIds: Array<string>, contentId: string): Array<string> => {
  return [...pinnedContentIds.filter((id) => id !== contentId)];
};

const addContactIdToContentGroupPinnedContentIds = (pinnedContentIds: Array<string>, contentId: string): Array<string> => {
  return Array.from(new Set([contentId, ...pinnedContentIds]));
};
